import React from 'react';
import { useData } from '../../contexts/DataContext';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const { FiTrendingUp, FiHome, FiBookOpen, FiPlus, FiArrowRight } = FiIcons;

const PortfolioSummary = () => {
  const { scenarios, calculations } = useData();

  // Group calculations by type
  const calculationsByType = calculations.reduce((acc, calc) => {
    acc[calc.type] = (acc[calc.type] || 0) + 1;
    return acc;
  }, {});

  const chartData = [
    { name: 'Retirement', value: calculationsByType.retirement || 0, color: '#D4AF37' },
    { name: 'Home Purchase', value: calculationsByType.home || 0, color: '#FFFFFF' },
    { name: 'Education', value: calculationsByType.education || 0, color: '#999999' }
  ].filter(item => item.value > 0);

  // Recent activity timeline
  const recentActivity = [...calculations, ...scenarios]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const calculatorLinks = [
    {
      title: 'Retirement Planning',
      description: 'Plan for your retirement goals',
      icon: FiTrendingUp,
      link: '/calculators/retirement',
      color: 'bg-primary-500'
    },
    {
      title: 'Home Purchase',
      description: 'Calculate mortgage and affordability',
      icon: FiHome,
      link: '/calculators/home',
      color: 'bg-primary-500'
    },
    {
      title: 'Education Planning',
      description: 'Save for education expenses',
      icon: FiBookOpen,
      link: '/calculators/education',
      color: 'bg-primary-500'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {calculatorLinks.map((calculator) => (
          <Link
            key={calculator.title}
            to={calculator.link}
            className="bg-secondary-900 rounded-xl shadow-lg p-6 hover:shadow-gold transition-all duration-300 group border border-primary-500/20"
          >
            <div className={`w-12 h-12 ${calculator.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
              <SafeIcon icon={calculator.icon} className="w-6 h-6 text-black" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {calculator.title}
            </h3>
            <p className="text-secondary-400 mb-4">
              {calculator.description}
            </p>
            <div className="flex items-center text-primary-500 group-hover:text-primary-400">
              <span className="text-sm font-medium">Start Planning</span>
              <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calculation Distribution */}
        <div className="bg-secondary-900 rounded-xl shadow-lg p-6 border border-primary-500/20">
          <h3 className="text-xl font-bold text-white mb-6">
            Calculation Distribution
          </h3>
          
          {chartData.length > 0 ? (
            <>
              <div className="h-64 mb-4 bg-black/50 p-4 rounded-lg">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#222', borderColor: '#D4AF37', color: '#fff' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-2">
                {chartData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm text-white">{item.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-primary-500">{item.value} calculations</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <SafeIcon icon={FiTrendingUp} className="w-16 h-16 text-primary-500/30 mx-auto mb-4" />
              <p className="text-secondary-400">No calculations yet. Start planning!</p>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-secondary-900 rounded-xl shadow-lg p-6 border border-primary-500/20">
          <h3 className="text-xl font-bold text-white mb-6">
            Recent Activity
          </h3>
          
          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-black rounded-lg border border-primary-500/10">
                  <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon 
                      icon={item.type === 'retirement' ? FiTrendingUp : 
                            item.type === 'home' ? FiHome : 
                            item.type === 'education' ? FiBookOpen : FiPlus} 
                      className="w-4 h-4 text-primary-500" 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">
                      {item.name || `${item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : 'New'} ${item.results ? 'Calculation' : 'Scenario'}`}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <SafeIcon icon={FiPlus} className="w-16 h-16 text-primary-500/30 mx-auto mb-4" />
              <p className="text-secondary-400">No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-secondary-900 rounded-xl shadow-lg p-6 border border-primary-500/20">
        <h3 className="text-xl font-bold text-white mb-6">
          Planning Summary
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-primary-500" />
            </div>
            <p className="text-2xl font-bold text-primary-500">
              {calculationsByType.retirement || 0}
            </p>
            <p className="text-sm text-secondary-400">Retirement Plans</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <SafeIcon icon={FiHome} className="w-6 h-6 text-primary-500" />
            </div>
            <p className="text-2xl font-bold text-primary-500">
              {calculationsByType.home || 0}
            </p>
            <p className="text-sm text-secondary-400">Home Calculations</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-primary-500" />
            </div>
            <p className="text-2xl font-bold text-primary-500">
              {calculationsByType.education || 0}
            </p>
            <p className="text-sm text-secondary-400">Education Plans</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <SafeIcon icon={FiPlus} className="w-6 h-6 text-primary-500" />
            </div>
            <p className="text-2xl font-bold text-primary-500">
              {scenarios.length}
            </p>
            <p className="text-sm text-secondary-400">Saved Scenarios</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSummary;